// @flow
import React, { useState } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import ViewArrayRoundedIcon from '@material-ui/icons/ViewArrayRounded';
import strings from 'strings';
import { FPIconButton, When } from 'UI/components/atoms';
import TabPanel from 'UI/components/atoms/TabPanel';
import { colors } from 'UI/res';

import { OrganizeTabsDrawer } from './OrganizeTabsDrawer';
import { useTabsHandler } from './TabsView.hooks';
import { useStyles, useTabContentStyles, useTabStyles } from './TabsView.styled';
import {
  a11yProps,
  buildInitialOrder,
  checkForIllegalModeChange,
  ComponentStateMode,
  getModeFromProps
} from './TabsView.utils';

export type TabsViewProps = {
  className?: string,
  content: 'start' | 'center' | 'block',
  enableUrlNavigation?: boolean,
  initialTab?: number | string,
  onChangeTabIndex?: any => any,
  organizationStorageKey?: string | null,
  organizationSubheader?: string,
  padding?: number | string | null,
  panelHeight?: number | string | null,
  paramKey?: string,
  selectedTab?: number,
  tabPanelProps: Object,
  tabProps: Object,
  tabs: Array<Object>,
  tabsContainerProps: Object,
  useIndexes: boolean // To change default behavior of using the selected tab value as an index, and instead use any given value. Useful when tabs order needs to be dynamic and logic depends on the current tab
};

const TabsView = (props: TabsViewProps) => {
  const {
    content,
    enableUrlNavigation,
    initialTab,
    onChangeTabIndex,
    organizationStorageKey,
    organizationSubheader,
    padding,
    panelHeight,
    paramKey,
    selectedTab: selectedTabProp,
    tabPanelProps,
    tabProps,
    tabs,
    tabsContainerProps,
    useIndexes,
    ...rest
  } = props;

  /** Styles settings */
  const classes = useStyles();
  const tabClasses = useTabStyles();
  const tabContentClasses = useTabContentStyles({ panelHeight, padding });

  const [stateMode] = useState(() => getModeFromProps(props));
  checkForIllegalModeChange(props, stateMode);

  const [orderedTabs, setOrderedTabs] = useState(() =>
    buildInitialOrder({ organizationStorageKey, tabs })
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  const firstTab = !useIndexes && orderedTabs.length > 0 ? orderedTabs[0].id : 0;
  const initialValue =
    stateMode === ComponentStateMode.Uncontrolled ? initialTab || firstTab : null;

  const { handleTabChange: setInternalValue, selectedTab: internalValue } = useTabsHandler(
    orderedTabs,
    {
      enableUrlNavigation,
      useIndexes,
      initialValue,
      paramKey
    }
  );

  const currentValue =
    stateMode === ComponentStateMode.Uncontrolled ? internalValue : selectedTabProp;

  const selectedTab = useIndexes ? Number(currentValue) : currentValue;
  const visibleTabs = orderedTabs.filter(({ visible = true }) => visible);

  const handleTabChange = (event, newTab = 0) => {
    if (stateMode === ComponentStateMode.Controlled) {
      onChangeTabIndex && onChangeTabIndex(event, newTab);
    } else {
      setInternalValue(event, newTab);
    }
  };

  const handleSaveOrder = updatedTabs => {
    setOrderedTabs(updatedTabs);
    setDrawerOpen(false);
  };

  return (
    <div className={tabClasses.wrapper} {...rest}>
      <AppBar classes={classes} position="static" color="default">
        <Box display="flex" alignItems="center" width="100%">
          <Tabs
            aria-label="tabs"
            className={tabClasses.fullWidth}
            indicatorColor="primary"
            onChange={handleTabChange}
            scrollButtons="on"
            textColor="primary"
            value={selectedTab}
            variant="scrollable"
            {...tabsContainerProps}
          >
            {visibleTabs.map((tab, index) => {
              const { icon, id, label } = tab;
              const isTabSelected = selectedTab === (useIndexes ? index : id);
              const styledIcon = {
                ...icon,
                props: { fill: isTabSelected ? colors.success : colors.iconInactive }
              };

              return (
                <Tab
                  className={tabClasses.tab}
                  icon={icon ? styledIcon : null}
                  key={label}
                  label={label}
                  value={useIndexes ? index : id}
                  {...a11yProps({ id, index, label })}
                  {...tab.tabProps}
                  {...tabProps}
                />
              );
            })}
          </Tabs>

          <When condition={organizationStorageKey}>
            <FPIconButton
              onClick={() => setDrawerOpen(true)}
              tooltipProps={{
                title: strings.inventoryProfiles.sections.tabs.organize.title
              }}
            >
              <ViewArrayRoundedIcon />
            </FPIconButton>
          </When>
        </Box>
      </AppBar>

      {visibleTabs.map(({ id, label }, index) => {
        const tabToRender = tabs.find(t => (t?.id || t?.label) === (id || label)) || null;

        return tabToRender ? (
          <TabPanel
            classes={tabContentClasses}
            content={content}
            index={useIndexes ? index : id}
            key={tabToRender.label}
            value={selectedTab}
            {...tabToRender?.tabPanelProps}
            {...tabPanelProps}
          >
            {tabToRender.view}
          </TabPanel>
        ) : null;
      })}

      <When condition={organizationStorageKey && drawerOpen}>
        <OrganizeTabsDrawer
          onClose={() => setDrawerOpen(false)}
          onSave={handleSaveOrder}
          storageKey={organizationStorageKey}
          subheader={organizationSubheader}
          tabs={visibleTabs}
        />
      </When>
    </div>
  );
};

TabsView.defaultProps = {
  content: 'center',
  enableUrlNavigation: false,
  initialTab: undefined,
  onChangeTabIndex: undefined,
  padding: undefined,
  panelHeight: undefined,
  paramKey: 'tab',
  selectedTab: undefined,
  tabPanelProps: undefined,
  tabProps: undefined,
  tabsContainerProps: undefined,
  useIndexes: true
};

export default TabsView;
